/**
 * v136.0 — useIdentity React hook + provider.
 *
 * Wraps the postMessage identityBridge in a React context so any surface
 * (Range Log header, Settings panel, future Pro-gated CTAs) can read the
 * authenticated state without re-fetching.
 *
 * `identity.signedIn` is the gating boolean. If false, the UI shows the
 * anonymous Sign-In CTA. If true, surfaces can show signed-in chrome and
 * the cloud-sync hooks become safe to call.
 *
 * Reactivity:
 *   - Fetches once on mount, then caches via identityBridge's module-level
 *     cache.
 *   - Re-fetches when the page returns from a sign-in redirect (detected via
 *     `?signedin=1` query param + a manual refresh hook).
 *   - Listens for `storage` events on `sparrow.identity.invalidate` so a
 *     sign-out from another tab triggers a refresh.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  fetchIdentity,
  clearIdentityCache,
  type SparrowIdentity,
} from './identityBridge';

interface IdentityContextValue {
  identity: SparrowIdentity;
  loading: boolean;
  refresh: () => Promise<SparrowIdentity>;
}

const IdentityContext = createContext<IdentityContextValue | null>(null);

const INVALIDATE_STORAGE_KEY = 'sparrow.identity.invalidate';

export function IdentityProvider({ children }: { children: React.ReactNode }) {
  const [identity, setIdentity] = useState<SparrowIdentity>({ signedIn: false });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (): Promise<SparrowIdentity> => {
    clearIdentityCache();
    const id = await fetchIdentity();
    setIdentity(id);
    setLoading(false);
    return id;
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchIdentity()
      .then((id) => {
        if (cancelled) return;
        setIdentity(id);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Refetch on cross-tab sign-out (Settings panel writes the storage key when
  // the user signs out — the other tab picks it up via `storage` event).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === INVALIDATE_STORAGE_KEY) {
        void refresh();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refresh]);

  // v135.3 CRITICAL hotfix — cross-origin parent .location access throws
  // SecurityError on every iframe load (Forge runs on cdn.shopify.com,
  // storefront is sparrowexpeditions.com). The previous `?.` chain caught
  // window.parent being undefined but NOT the cross-origin throw. This
  // crashed the entire bundle before any UI rendered.
  //
  // Fallback: read the iframe's OWN location.search (Shopify forwards
  // certain query params through). It's the right intent — we're looking
  // for the ?signedin=1 signal after a sign-in redirect, which Shopify
  // appends to the iframe URL too. If parent access works in some future
  // first-party context, the try block uses it; otherwise we fall through
  // to the iframe's own search params.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let search = '';
    try {
      // Same-origin case (dev, standalone preview): read parent search.
      // Cross-origin (production iframe): this throws SecurityError; catch.
      search = window.parent?.location?.search ?? '';
    } catch {
      // Cross-origin parent. Use the iframe's own URL search instead.
      search = window.location?.search ?? '';
    }
    try {
      const params = new URLSearchParams(search);
      if (params.has('signedin')) void refresh();
    } catch {
      // URLSearchParams parsing should never throw, but guard anyway.
    }
  }, [refresh]);

  const value = useMemo<IdentityContextValue>(
    () => ({ identity, loading, refresh }),
    [identity, loading, refresh],
  );

  return <IdentityContext.Provider value={value}>{children}</IdentityContext.Provider>;
}

export function useIdentity(): IdentityContextValue {
  const ctx = useContext(IdentityContext);
  if (!ctx) {
    // Default value when called outside a provider — keeps the hook safe to use
    // in unit tests without a provider wrapper.
    return {
      identity: { signedIn: false },
      loading: false,
      refresh: async () => ({ signedIn: false }),
    };
  }
  return ctx;
}

/** Cross-tab invalidate signal (called from sign-out actions). */
export function broadcastIdentityInvalidate(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(INVALIDATE_STORAGE_KEY, String(Date.now()));
  } catch {
    // localStorage may throw in private modes — non-fatal.
  }
}
